import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import { Form, useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import * as StyledFormElements from '../../../StyledForm/StyledForm.elements';
import GetIcon from '../../../GetIcon';
import StepFooter from '../StepFooter';
import Stepper from '../Stepper';
import WarningClose from './components/WarningClose';
import environment from '../../../../utils/environment';
import tracking from '../../../../utils/tracking';
import useStepContainerStyles from './StepContainer.styles';
import { PublicRoutePath } from '../../../../utils/routes';
import { QuoteBuilderStep, QuoteBuilderValues } from '../../utils/formState';
import { TrackingEvent } from '../../../../utils/tracking/types';
import { authenticated$ } from '../../../../store/session/selectors';
import { fetchCataloguePerform } from '../../../../store/cart/actions';
import { setCurrentPageLeaver } from '../../utils/quoteTracking';
import { stepOrder, useCurrentUrlStep, usePreviousFormSteps, useStepTracker } from '../../QuoteBuilder.methods';

export interface StepContainerProps {
  children: React.ReactNode;
}

const stepConstants = (authenticated: boolean) => ({
  [QuoteBuilderStep.DETAILS]: {
    backButton: undefined,
    stepTitle: "Let's start with the basics",
    proceedBtnText: 'Choose your mix',
  },
  [QuoteBuilderStep.MIX]: {
    backButton: 'Back',
    stepTitle: "Now it's time to customise your mix",
    proceedBtnText: authenticated ? 'Save quote' : 'Add your details',
  },
  [QuoteBuilderStep.REGISTER]: {
    backButton: 'Back',
    stepTitle: 'Almost there! Just tell us where to send your quote',
    proceedBtnText: 'Verify mobile number',
  },
  [QuoteBuilderStep.MOBILE]: {
    backButton: 'Back',
    stepTitle: 'Almost there! Just tell us where to send your quote',
    proceedBtnText: 'Get your quote',
  },
});

const wrapper = environment.isClient ? window.sessionStorage.getItem('wrapper') : undefined;
const isEmbedded = wrapper === 'embedded';

function StepContainer({ children }: StepContainerProps) {
  const { push, pathname } = useRouter();

  const { classes } = useStepContainerStyles();
  const authenticated = useSelector(authenticated$);

  const { urlStep, urlStepIndex } = useCurrentUrlStep();
  const { prevStep, prevStepLink } = usePreviousFormSteps();

  const onPrevious = prevStepLink ? () => push(prevStepLink) : undefined;

  const dispatch = useDispatch();

  const sessionStepConstants = useMemo(() => stepConstants(authenticated), [authenticated]);

  const {
    values,
    status: currentStepIndex,
    setTouched,
    setSubmitting,
    setErrors,
  } = useFormikContext<QuoteBuilderValues>();

  useStepTracker(urlStep, prevStep);

  const [dialogOpen, setDialogOpen] = useState(false);

  const previousTracking = () => {
    if (urlStep === QuoteBuilderStep.DETAILS) setCurrentPageLeaver('details', 'quoteOrSignup');
    if (urlStep === QuoteBuilderStep.MIX) setCurrentPageLeaver('mix', 'region');
    if (urlStep === QuoteBuilderStep.REGISTER) setCurrentPageLeaver('register', 'mix');
    if (urlStep === QuoteBuilderStep.MOBILE) setCurrentPageLeaver('passcode', 'register');
  };

  const previous = () => {
    previousTracking();
    if (onPrevious) void onPrevious();
  };

  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);

  const continueDialog = () => {
    tracking.event(TrackingEvent.CLOSE_QUOTE);
    if (environment.isClient) window.sessionStorage.removeItem('quote-builder');

    void push(PublicRoutePath.HOME);
    if (urlStep === QuoteBuilderStep.DETAILS) setCurrentPageLeaver('region', 'home');
    if (urlStep === QuoteBuilderStep.MIX) setCurrentPageLeaver('mix', 'home');
    if (urlStep === QuoteBuilderStep.REGISTER) setCurrentPageLeaver('register', 'home');
    if (urlStep === QuoteBuilderStep.MOBILE) setCurrentPageLeaver('passcode', 'home');
  };

  // Should run everytime urlStep changes.
  useEffect(() => {
    // Reset these parts of the form (not global resetForm) when the urlStep changes, so we don't have to include values for resetForm in this effect.
    setTouched({});
    setSubmitting(false);
    setErrors({});
  }, [setErrors, setSubmitting, setTouched, urlStep]);

  const { location } = values;

  const locality = location?.locality;
  const postcode = location?.postcode;

  useEffect(() => {
    dispatch(fetchCataloguePerform(locality, postcode));
  }, [dispatch, locality, postcode]);

  const nonFirstPageTitle = (
    <>
      <Box display={{ xs: 'none', sm: 'inline' }}>
        <h1 className={classes.title}>Get a quote</h1>
      </Box>
      <Box display={{ xs: 'inline', sm: 'none' }}>
        <Button onClick={previous} className={classes.titleLink} variant="text" startIcon={<ChevronLeftIcon />}>
          Back
        </Button>
      </Box>
    </>
  );

  // If the user lands on a page before completing previous steps, send them back.
  if (!isEmbedded && !authenticated && environment.isClient) {
    if (urlStepIndex !== 0 && urlStepIndex >= currentStepIndex) {
      if (currentStepIndex === 0) void push(PublicRoutePath.QUOTE_PROCESS);

      if (stepOrder[currentStepIndex] !== pathname.replace(`${PublicRoutePath.QUOTE_PROCESS}/`, '')) {
        void push(`${PublicRoutePath.QUOTE_PROCESS}/${stepOrder[currentStepIndex]}`);
      }
    }
  }

  const currentStepConstants = sessionStepConstants[urlStep];

  return (
    <Form>
      {!isEmbedded && (
        <header className={classes.header}>
          {urlStepIndex === 0 ? <h1 className={classes.title}>Get a quote</h1> : nonFirstPageTitle}

          <Button onClick={openDialog} className={classes.closeLink} variant="text">
            <GetIcon icon="close" />
          </Button>

          <Stepper currentStep={urlStep} />
        </header>
      )}

      <WarningClose handleClose={closeDialog} handleContinue={continueDialog} isOpen={dialogOpen} />
      <StyledFormElements.Container>
        <Container className={isEmbedded ? classes.containerEmbedded : classes.container}>
          <Typography variant="h3" component="h1" className={classes.stepTitle}>
            {currentStepConstants.stepTitle}
          </Typography>
          {children}
        </Container>
      </StyledFormElements.Container>
      <StepFooter onPrevious={onPrevious ? previous : undefined} {...currentStepConstants} />
    </Form>
  );
}

export default StepContainer;
